import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

class VideoFootageService {
  async listFolders(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.page != null) searchParams.set('page', String(params.page));
    if (params.all != null) searchParams.set('all', params.all ? 'true' : 'false');
    if (params.search) searchParams.set('search', params.search);
    if (params.tag) searchParams.set('tag', params.tag);
    const qs = searchParams.toString();
    const response = await axios.get(`${API_BASE_URL}/video-footage/folders${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async getFolder(folderId) {
    const response = await axios.get(`${API_BASE_URL}/video-footage/folders/${folderId}`);
    return response.data;
  }

  async createFolder({ name, ownerIds, tags }) {
    const response = await axios.post(`${API_BASE_URL}/video-footage/folders`, {
      name,
      ownerIds,
      tags,
    });
    return response.data;
  }

  async updateFolder(folderId, { name, ownerIds, tags }) {
    const payload = {};
    if (name !== undefined) payload.name = name;
    if (ownerIds !== undefined) payload.ownerIds = ownerIds;
    if (tags !== undefined) payload.tags = tags;
    const response = await axios.patch(`${API_BASE_URL}/video-footage/folders/${folderId}`, payload);
    return response.data;
  }

  async deleteFolder(folderId) {
    const response = await axios.delete(`${API_BASE_URL}/video-footage/folders/${folderId}`);
    return response.data;
  }

  async requestCutSourcePresignedUrl({ fileName, fileSize, contentType }) {
    const response = await axios.post(
      `${API_BASE_URL}/video-footage/cut-jobs/presigned-url`,
      { fileName, fileSize, contentType: contentType || 'video/mp4' },
    );
    return response.data;
  }

  async startCutJob({
    folderId,
    sourceObjectName,
    fileName,
    applyFadeInFadeOut,
    compressOutput,
  }) {
    const response = await axios.post(`${API_BASE_URL}/video-footage/cut-jobs`, {
      folderId,
      sourceObjectName,
      fileName,
      applyFadeInFadeOut,
      compressOutput,
    });
    return response.data;
  }

  async getCutJob(jobId) {
    const response = await axios.get(`${API_BASE_URL}/video-footage/cut-jobs/${jobId}`);
    return response.data;
  }

  async listVideos(folderId, params = {}) {
    const searchParams = new URLSearchParams();
    if (params.page != null) searchParams.set('page', String(params.page));
    if (params.search) searchParams.set('search', params.search);
    const qs = searchParams.toString();
    const response = await axios.get(
      `${API_BASE_URL}/video-footage/folders/${folderId}/videos${qs ? `?${qs}` : ''}`,
    );
    return response.data;
  }

  async requestVideoPresignedUrl(folderId, { fileName, fileSize, contentType }) {
    const response = await axios.post(
      `${API_BASE_URL}/video-footage/folders/${folderId}/videos/presigned-url`,
      { fileName, fileSize, contentType: contentType || 'video/mp4' },
    );
    return response.data;
  }

  async completeVideoUpload(folderId, { objectName, fileSize, fileName, contentType }) {
    const response = await axios.post(
      `${API_BASE_URL}/video-footage/folders/${folderId}/videos/complete`,
      { objectName, fileSize, fileName, contentType },
    );
    return response.data;
  }

  async getPlayUrl(folderId, videoId) {
    const response = await axios.get(
      `${API_BASE_URL}/video-footage/folders/${folderId}/videos/${videoId}/play-url`,
    );
    return response.data;
  }

  async applyFadeInOutToMissingVideos(folderId) {
    const response = await axios.post(
      `${API_BASE_URL}/video-footage/folders/${folderId}/videos/apply-fade-in-out`,
      {},
    );
    return response.data;
  }

  async compressVideos(folderId) {
    const response = await axios.post(
      `${API_BASE_URL}/video-footage/folders/${folderId}/videos/compress`,
      {},
    );
    return response.data;
  }

  async getFadeJob(folderId, jobId) {
    const response = await axios.get(
      `${API_BASE_URL}/video-footage/folders/${folderId}/videos/fade-jobs/${jobId}`,
    );
    return response.data;
  }

  async markAllFadeInOut(folderId) {
    const response = await axios.post(
      `${API_BASE_URL}/video-footage/folders/${folderId}/videos/mark-all-fade-in-out`,
    );
    return response.data;
  }

  async moveAllVideos(folderId, destinationFolderId) {
    const response = await axios.post(
      `${API_BASE_URL}/video-footage/folders/${folderId}/videos/move-all`,
      { destinationFolderId },
    );
    return response.data;
  }

  async deleteVideo(folderId, videoId) {
    const response = await axios.delete(
      `${API_BASE_URL}/video-footage/folders/${folderId}/videos/${videoId}`,
    );
    return response.data;
  }
}

export default new VideoFootageService();
